import { alpha } from '@mui/material/styles';

function createGradient(color1: string, color2: string) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

interface GradientsPaletteOptions {
  primary: string;
  info: string;
  success: string;
  warning: string;
  error: string;
  ai: string;
  aiHover: string;
}

declare module '@mui/material/styles' {
  interface Palette {
    ai: PaletteColor;
  }
  interface PaletteOptions {
    ai?: SimplePaletteColorOptions;
  }
}

export const ALPHAS = {
  subtle: 0.04, // barely-there tint (button hover on neutral)
  hover: 0.08, // standard hover state tint
  selected: 0.12, // selected / active tint
  chip: 0.1, // filled chip background
  chipHover: 0.16, // filled chip hover background
  border: 0.24, // colored border / button-group separator
  backdrop: 0.48 // overlay / backdrop
} as const;

declare module '@mui/material/styles' {
  interface Theme {
    alphas: typeof ALPHAS;
  }
  interface ThemeOptions {
    alphas?: Partial<typeof ALPHAS>;
  }
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
  interface Palette {
    gradients: GradientsPaletteOptions;
  }
  interface PaletteOptions {
    gradients?: GradientsPaletteOptions;
  }
}

declare module '@mui/material' {
  interface Color {
    0: string;
    500_8: string;
    500_12: string;
    500_16: string;
    500_24: string;
    500_32: string;
    500_48: string;
    500_56: string;
    500_80: string;
  }
}

// Worknity secondary: 1E4466
// Worknity primary: 7CBA74
// SETUP COLORS
const PRIMARY = {
  lighter: '#E6F6EC',
  light: '#ACE2BE',
  main: '#2E9E5B',
  dark: '#237A46',
  darker: '#195A34'
};
const SECONDARY = {
  lighter: '#D6E4EE',
  light: '#7AAAC4',
  main: '#0b4569',
  dark: '#083552',
  darker: '#052538'
};
const INFO = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A'
};
// Emerald green — harmonious with primary forest green, distinct from lime
const SUCCESS = {
  lighter: '#DCFCE7',
  light: '#86EFAC',
  main: '#22C55E',
  dark: '#15803D',
  darker: '#14532D'
};
// AI brand color — purple/indigo, used for all AI-powered features
const AI = {
  lighter: '#EDE9FE',
  light: '#A78BFA',
  main: '#7C3AED',
  dark: '#6D28D9',
  darker: '#4C1D95'
};
const WARNING = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01'
};
const ERROR = {
  lighter: '#FFE7D9',
  light: '#FFA48D',
  main: '#FF4842',
  dark: '#B72136',
  darker: '#7A0C2E'
};
const GREY = {
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8)
};
// Mastery colors as flat strings for use in non-sx contexts (e.g. landing page preview)
export const MASTERY_DOT_COLORS: Record<string, string> = {
  mastered: PRIMARY.main,
  learning: WARNING.main,
  needs_review: ERROR.main,
  not_started: GREY[400]
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
  ai: `linear-gradient(135deg, ${AI.main} 0%, #4f46e5 100%)`,
  aiHover: `linear-gradient(135deg, ${AI.dark} 0%, #4338ca 100%)`
};
const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: { ...PRIMARY, contrastText: '#fff' },
  secondary: { ...SECONDARY, contrastText: '#fff' },
  info: { ...INFO, contrastText: '#fff' },
  success: { ...SUCCESS, contrastText: '#fff' },
  warning: { ...WARNING, contrastText: GREY[800] },
  error: { ...ERROR, contrastText: '#fff' },
  ai: { ...AI, contrastText: '#fff' },
  grey: GREY,
  gradients: GRADIENTS,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48
  }
};

const palette = {
  light: {
    ...COMMON,
    text: { primary: '#111827', secondary: GREY[700], disabled: GREY[500] },
    background: { paper: '#ffffff', default: '#ffffff', neutral: GREY[100] },
    action: { active: GREY[600], ...COMMON.action }
  },
  dark: {
    ...COMMON,
    text: { primary: '#fff', secondary: GREY[500], disabled: GREY[600] },
    background: { paper: GREY[800], default: GREY[900], neutral: GREY[500_16] },
    action: { active: GREY[500], ...COMMON.action }
  }
};

export default palette;
